import * as THREE from 'three/webgpu';

import {
    isSketchArcObject,
    isSketchCircleObject,
    isSketchEllipseObject,
    isSketchLineObject,
    isSketchParabolaObject,
    isSketchSlotObject,
    isSketchSplineObject,
    isSketchStyleSplineObject,
} from '../../mesh';
import type { RawPointerEventPayload } from '../../viewer/moudles/EventManager';
import type { EntitySelectionManager } from '../EntitySelectionManager';
import { SketchEntitySelectionHandler } from './SketchEntitySelectionHandler';

/**
 * 判断目标是否为等距实体可操作的草图实体。
 *
 * 说明：
 * 1. 与 SketchOffsetController 中的 OffsetCandidate 类型对齐；
 * 2. 槽口等复合实体在拾取层面仍以其内部边（SketchLineObject）命中，
 *    槽口整体拾取由父类的 click 渲染管线自动提升。
 *
 * @param object 三维对象实例。
 * @returns 是否可参与等距偏移。
 */
function isOffsetCandidate(object: THREE.Object3D): boolean {
    return isSketchLineObject(object)
        || isSketchArcObject(object)
        || isSketchCircleObject(object)
        || isSketchEllipseObject(object)
        || isSketchParabolaObject(object)
        || isSketchSplineObject(object)
        || isSketchStyleSplineObject(object)
        || isSketchSlotObject(object);
}

/**
 * 等距实体模式专用选择处理类。
 *
 * 职责：
 * 1. 继承草图默认处理器基础能力；
 * 2. 等距实体态下响应「点击选择草图实体」「悬停高亮」与「左键框选多选」；
 * 3. 框选候选收口到可偏移实体（OffsetCandidate），并禁止点/轮廓拖拽等编辑行为。
 */
export class SketchOffsetEntitySelectionHandler extends SketchEntitySelectionHandler {
    /**
     * 构造函数。
     * @param entitySelectionManager 实体选择管理器。
     */
    public constructor(entitySelectionManager: EntitySelectionManager) {
        super(entitySelectionManager);
    }

    /**
     * 处理鼠标按下。
     *
     * 设计说明：
     * 1. 等距实体是草图内命令会话，不启用普通草图点/轮廓拖拽，但需要记录左键起点以支持框选；
     * 2. 拖拽行为全部保留给框选（与 SolidWorks 命令会话期间拖拽绘制选择框的语义一致）。
     *
     * @param payload 原始事件。
     */
    public override onMouseDown(payload: RawPointerEventPayload): void {
        if (payload.button !== 0) {
            return;
        }

        //------ 等距实体框选起点记录 开始------
        this.entitySelectionManager.setMouseDownStart(new THREE.Vector2(payload.clientX, payload.clientY));
        this.entitySelectionManager.setIsBoxDragging(false);
        this.entitySelectionManager.setBoxDragEnd(null);
        this.resetSelectionBoxVisual();
        this.entitySelectionManager.setSuppressNextClick(false);
        //------ 等距实体框选起点记录 结束------
    }

    /**
     * 处理鼠标移动。
     *
     * 说明：
     * 1. 先检测框选拖动：左键按下且位移超过阈值时进入框选并更新选框可视化；
     * 2. 未进入框选拖动时复用父类的 pickEntityAt + collectHoverSelectionTargets，
     *    与普通草图共享候选过滤；
     * 3. 通过 updateHoverState 驱动 InteractionStateManager 更新视觉层高亮。
     *
     * @param payload 原始事件。
     */
    public override onMouseMove(payload: RawPointerEventPayload): void {
        //------ 等距实体框选拖动检测 开始------
        const mouseDownStart = this.entitySelectionManager.getMouseDownStart();
        if (mouseDownStart && payload.buttons !== 0) {
            const dx = payload.clientX - mouseDownStart.x;
            const dy = payload.clientY - mouseDownStart.y;
            const distance = Math.sqrt(dx * dx + dy * dy);
            if (distance > this.entitySelectionManager.getBoxDragThreshold() || this.entitySelectionManager.getIsBoxDragging()) {
                this.entitySelectionManager.setIsBoxDragging(true);
                this.entitySelectionManager.setBoxDragEnd(new THREE.Vector2(payload.clientX, payload.clientY));
                this.clearHoverState();
                this.updateSelectionBoxVisual(mouseDownStart, new THREE.Vector2(payload.clientX, payload.clientY));
                return;
            }
        }

        // 按键拖动阶段不执行 hover 拾取，避免与框选互相干扰。
        if (payload.buttons !== 0) {
            this.clearHoverState();
            return;
        }

        // 当所有鼠标按钮都释放时，清空 mouseDownStart，防止框选基于过期起点被意外触发
        if (payload.buttons === 0 && mouseDownStart) {
            this.entitySelectionManager.setMouseDownStart(null);
            this.entitySelectionManager.setIsBoxDragging(false);
            this.entitySelectionManager.setBoxDragEnd(null);
            this.resetSelectionBoxVisual();
        }
        //------ 等距实体框选拖动检测 结束------

        const hoveredPick = this.pickEntityAt(
            payload.clientX,
            payload.clientY,
            this.collectHoverSelectionTargets(),
        );
        this.updateHoverState(this.resolveHoverPickWithSelectionExclusion(hoveredPick));
    }

    /**
     * 处理鼠标抬起。
     *
     * 说明：等距实体框选结算：位移超过阈值时执行 performBoxSelection，
     * 并抑制随后派发的 click，避免一次框选被误结算为点击选择。
     *
     * @param payload 原始事件。
     */
    public override onMouseUp(payload: RawPointerEventPayload): void {
        if (payload.button !== 0) {
            return;
        }

        //------ 等距实体框选结算 开始------
        const dragStart = this.entitySelectionManager.getMouseDownStart();
        if (!dragStart) {
            return;
        }

        const isBoxDragging = this.entitySelectionManager.getIsBoxDragging();
        this.entitySelectionManager.setMouseDownStart(null);
        this.entitySelectionManager.setIsBoxDragging(false);
        this.entitySelectionManager.setBoxDragEnd(null);
        this.resetSelectionBoxVisual();

        if (isBoxDragging) {
            // 左键拖动统一执行框选；Ctrl 只影响点击多选，不再改变框选的结算语义。
            this.performBoxSelection(dragStart, new THREE.Vector2(payload.clientX, payload.clientY), false);
            this.entitySelectionManager.setSuppressNextClick(true);
        }
        //------ 等距实体框选结算 结束------
    }

    /**
     * 收集框选候选对象。
     *
     * 设计说明：
     * 1. 父类（SketchEntitySelectionHandler）已在候选阶段完成活动草图归属与基础草图类型过滤；
     * 2. 这里在父类结果上再收口到可偏移实体（OffsetCandidate），
     *    保证等距实体框选只会选中可参与偏移的草图实体，不会误选点、轮廓等；
     * 3. 槽口以内部边（SketchLineObject）命中，与点击选择行为保持一致。
     *
     * @returns 可参与等距偏移的候选对象列表。
     */
    protected override collectBoxSelectionTargets(): THREE.Object3D[] {
        return super.collectBoxSelectionTargets().filter((object) => isOffsetCandidate(object));
    }

    /**
     * 处理鼠标点击——选中/取消选中草图实体。
     *
     * 说明：
     * 1. 只有左键点击生效；
     * 2. 射线拾取后过滤只保留可偏移的草图实体类型（OffsetCandidate）；
     * 3. 已选中的实体再次点击取消选中，未选中的追加到选中集（toggle + 多选）；
     * 4. 选中结果通过 EntitySelectionManager.updateSelectionState 发布，
     *    SketchOffsetController 通过订阅 SelectionUpdated 事件消费。
     *
     * @param payload 原始事件。
     */
    public override onClick(payload: RawPointerEventPayload): void {
        if (payload.button !== 0) {
            return;
        }

        const picked = this.resolveClickSelectionPick(this.pickEntityAt(
            payload.clientX,
            payload.clientY,
            this.collectClickSelectionTargets(),
        ));
        if (!picked) {
            return;
        }

        // 过滤点击结果，只保留可偏移的草图实体
        if (!isOffsetCandidate(picked.object)) {
            return;
        }

        // 获取当前已选中的可偏移实体列表（排除非偏移类型，如 Point 等）
        const currentSelectedPicks = this.entitySelectionManager.getSelectedPicks()
            .filter((item) => isOffsetCandidate(item.object));

        // toggle 选中：已选中则移除，未选中则追加
        const isAlreadySelected = currentSelectedPicks.some(
            (item) => item.object.uuid === picked.object.uuid,
        );
        const nextSelectedPicks = isAlreadySelected
            ? currentSelectedPicks.filter((item) => item.object.uuid !== picked.object.uuid)
            : [...currentSelectedPicks, picked];

        this.entitySelectionManager.updateSelectionState(nextSelectedPicks);
    }
}
